import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * System that manages the dynamic loading of face decoration assets.
 *
 * Ensures eye and mouth spritesheets are loaded on demand based on the
 * styling defined in an entity's FaceDecoration component.
 *
 * Rendering and animation synchronization are handled by RepresentationService.
 */
export class FaceDecorationSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['FaceDecoration', 'SpritesheetSprite'];

  // Track currently requested styles per entity to avoid redundant load calls.
  // We include a timestamp to handle hot-reloads without clearing the entire map,
  // which prevents duplicate registerOnEntityDestroyed callbacks.
  private requestedStyles = new Map<
    number,
    { eyes: string; mouth: string; body: string; timestamp: number }
  >();

  private currentHotReloadTimestamp = 0;

  run(deltaTime: number): void {
    // Check for hot-reload events once per tick to track the latest global timestamp.
    const hotReloadEvents = this.clientServices.common.eventBus.getEventsOfType('HotReload');
    if (hotReloadEvents.length > 0) {
      // Use the latest timestamp from the event stream
      this.currentHotReloadTimestamp =
        hotReloadEvents[hotReloadEvents.length - 1].payload.timestamp;
    }

    super.run(deltaTime);
  }

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    const faceDecoration = world.getComponent(entityId, 'FaceDecoration')!;
    const bodySpritesheet = world.getComponent(entityId, 'SpritesheetSprite')!;

    const spritesheetService = this.clientServices.spritesheet;

    // Check if we need to request loads
    const lastRequested = this.requestedStyles.get(entityId);

    const needsReload =
      !lastRequested ||
      lastRequested.eyes !== faceDecoration.eyesStyle ||
      lastRequested.mouth !== faceDecoration.mouthStyle ||
      lastRequested.body !== bodySpritesheet.spritesheetId ||
      lastRequested.timestamp < this.currentHotReloadTimestamp;

    if (needsReload) {
      if (!lastRequested) {
        // Only register cleanup once when the entity is first processed by this system.
        // This ensures the record is removed from the local tracking map upon destruction.
        world.registerOnEntityDestroyed(entityId, (id) => {
          this.requestedStyles.delete(id);
        });
      }

      // Record new request state
      this.requestedStyles.set(entityId, {
        eyes: faceDecoration.eyesStyle,
        mouth: faceDecoration.mouthStyle,
        body: bodySpritesheet.spritesheetId,
        timestamp: this.currentHotReloadTimestamp,
      });

      // Trigger asynchronous loads
      void spritesheetService.loadSpritesheet(faceDecoration.eyesStyle);
      void spritesheetService.loadPoint(faceDecoration.eyesStyle, 'EyePoint');

      void spritesheetService.loadSpritesheet(faceDecoration.mouthStyle);
      void spritesheetService.loadPoint(faceDecoration.mouthStyle, 'MouthPoint');

      void spritesheetService.loadPointsForSpritesheet(bodySpritesheet.spritesheetId, [
        'EyePoint',
        'MouthPoint',
      ]);
    }
  }
}
